import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { path: "/admin", label: "Dashboard", icon: "ri-dashboard-line", exact: true },
  { path: "/admin/productos", label: "Productos", icon: "ri-cake-line" },
  { path: "/admin/favoritos", label: "Favoritos del Mes", icon: "ri-star-line" },
  { path: "/admin/promociones", label: "Promociones", icon: "ri-price-tag-3-line" },
  { path: "/admin/blog", label: "Blog", icon: "ri-article-line" },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    sessionStorage.removeItem("sp_admin_auth");
    navigate("/admin/login");
  };

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#f8f4ff] flex">
      {/* Sidebar overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-[#7c5cbf] to-[#5a3e9a] z-30 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:z-auto`}
      >
        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/20">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#d4607a]">
              <i className="ri-cake-2-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-tight">
                San7o Pecado
              </p>
              <p className="text-[#e0d0ff] text-xs">Panel Admin</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-5 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                isActive(item.path, item.exact)
                  ? "bg-white text-[#7c5cbf]"
                  : "text-white/80 hover:bg-white/15 hover:text-white"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-base`}></i>
              </span>
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Footer sidebar */}
        <div className="px-3 py-4 border-t border-white/20 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/80 hover:bg-white/15 hover:text-white transition-colors cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-external-link-line text-base"></i>
            </span>
            Ver Sitio Web
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/80 hover:bg-[#d4607a]/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-line text-base"></i>
            </span>
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-[#c9b8f0]/30 px-4 md:px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f8f4ff] cursor-pointer transition-colors"
            >
              <i className="ri-menu-line text-[#7c5cbf] text-lg"></i>
            </button>
            <h2 className="text-base font-semibold text-[#2d1a0e]">
              {navItems.find((n) => isActive(n.path, n.exact))?.label ?? "Admin"}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#d4607a]/10">
              <i className="ri-user-line text-[#d4607a] text-sm"></i>
            </div>
            <span className="text-sm text-[#6b5b6e] hidden sm:block">Administrador</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
